/**
 * Query hook for fetching on-chain identity data
 * Uses TanStack Query for caching with appropriate stale times
 */

import { useQuery } from '@tanstack/react-query';
import { fetchIdentity } from '@/_services/identity.service.client';
import type { OnChainIdentity } from '@/types';
import { isValidSS58Address } from '@/_utils/ss58';
import { queryKeys } from './queryKeys';

/**
 * Hook to fetch on-chain identity for an SS58 address
 *
 * @param address - Optional SS58 address to look up
 * @returns TanStack Query result with identity data
 *
 * @example
 * ```tsx
 * function AddressWithIdentity({ address }: { address: string }) {
 *   const { data: identity, isLoading } = useIdentityQuery(address);
 *
 *   return (
 *     <span>
 *       {identity?.displayName ?? address}
 *       {identity?.isVerified && <VerifiedBadge />}
 *     </span>
 *   );
 * }
 * ```
 */
export function useIdentityQuery(address: string | null) {
	return useQuery<OnChainIdentity | null, Error>({
		queryKey: queryKeys.identity.byAddress(address!),
		queryFn: () => fetchIdentity(address!),
		// Only enable if we have a valid SS58 address
		enabled: !!address && isValidSS58Address(address),
		// Identity data changes infrequently - cache for 5 minutes
		staleTime: 5 * 60 * 1000,
		// Keep in cache for 30 minutes even after unmount
		gcTime: 30 * 60 * 1000,
		// Retry twice with 1 second delay (light client might need time to sync)
		retry: 2,
		retryDelay: 1000,
		// Don't refetch on window focus for identity data
		refetchOnWindowFocus: false
	});
}
